import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Clock, HelpCircle, Trophy, Sparkles, Loader2, ChevronRight, FileText, Zap } from 'lucide-react';
import { assessmentApi, categoryApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

// Exam configurations for the UI
const EXAM_OPTIONS = {
  PGCET: {
    name: 'PGCET',
    fullName: 'Karnataka PGCET',
    questions: 100,
    time: 90,
    sections: 'Verbal (25) + Quant (25) + Reasoning (25) + GK (25)',
    hasPYQ: true,
    pyqYears: [2025, 2024, 2023, 2022, 2021],
  },
  MAT: {
    name: 'MAT',
    fullName: 'Management Aptitude Test',
    questions: 200,
    time: 150,
    sections: 'Language (40) + Intelligence (40) + Math (40) + Data (40) + GK (40)',
    hasPYQ: false,
    pyqYears: [],
  },
};

function MockTests() {
  const [assessments, setAssessments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [myAttempts, setMyAttempts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('generate');
  const [selectedExam, setSelectedExam] = useState('PGCET');
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Generator state
  const [selectedDifficulty, setSelectedDifficulty] = useState('medium');
  const [generating, setGenerating] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'tests') loadAssessments();
  }, [selectedCategory, activeTab]);

  const loadData = async () => {
    try {
      const [catRes, attemptsRes, assRes] = await Promise.all([
        categoryApi.list().catch(() => ({ data: [] })),
        assessmentApi.getMyAttempts().catch(() => ({ data: [] })),
        assessmentApi.getAssessments().catch(() => ({ data: [] }))
      ]);
      setCategories(catRes.data || []);
      setMyAttempts(attemptsRes.data || []);
      setAssessments(assRes.data || []);
    } catch (error) {
      // continue
    } finally {
      setIsLoading(false);
    }
  };

  const loadAssessments = async () => {
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      const res = await assessmentApi.getAssessments(params);
      setAssessments(res.data || []);
    } catch (error) {
      toast.error('Failed to load mock tests');
    }
  };

  const currentExam = EXAM_OPTIONS[selectedExam];

  const handleGeneratePYQ = async (year) => {
    setGenerating(true);
    setGeneratingLabel(`${selectedExam} ${year} Paper`);
    try {
      const matchCat = categories.find(c =>
        c.slug === 'mba' || c.slug === 'mba-entrance' || c.name.toLowerCase().includes('mba')
      );

      const res = await assessmentApi.generatePYQ({
        exam: selectedExam,
        year: year,
        category_id: matchCat?.id || null,
      });

      toast.success(`${res.data.questionCount} questions loaded!`);
      navigate(`/lms/assessments/${res.data.id}`);
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to load paper. Try again.';
      toast.error(msg);
    } finally {
      setGenerating(false);
      setGeneratingLabel('');
    }
  };

  const handleGeneratePractice = async () => {
    setGenerating(true);
    setGeneratingLabel(`${selectedExam} Practice Paper`);
    try {
      const matchCat = categories.find(c =>
        c.slug === 'mba' || c.slug === 'mba-entrance' || c.name.toLowerCase().includes('mba')
      );

      const res = await assessmentApi.generatePYQ({
        exam: selectedExam,
        category_id: matchCat?.id || null,
        difficulty: selectedDifficulty,
      });

      toast.success(`${res.data.questionCount} questions generated!`);
      navigate(`/lms/assessments/${res.data.id}`);
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to generate. Try again.';
      toast.error(msg);
    } finally {
      setGenerating(false);
      setGeneratingLabel('');
    }
  };

  const getAttemptForAssessment = (assessmentId) => {
    return myAttempts.filter(a => a.assessment_id === assessmentId);
  };

  const getBestScore = (attempts) => {
    if (!attempts.length) return null;
    const graded = attempts.filter(a => a.status === 'graded' && a.score !== null);
    if (!graded.length) return null;
    return Math.max(...graded.map(a => a.max_score > 0 ? Math.round((a.score / a.max_score) * 100) : 0));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">MBA Mock Tests</h1>
        <p className="text-gray-600 mt-1">Practice with real previous year papers or AI-generated mock tests</p>
      </div>

      {/* Exam Selector */}
      <div className="flex gap-3 mb-6">
        {Object.entries(EXAM_OPTIONS).map(([key, exam]) => (
          <button
            key={key}
            onClick={() => setSelectedExam(key)}
            className={`px-5 py-3 rounded-xl font-medium transition-all border-2 ${
              selectedExam === key
                ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="text-sm font-bold">{exam.name}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{exam.questions} Qs • {exam.time} min</div>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('generate')}
          className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
            activeTab === 'generate'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Sparkles className="w-4 h-4 mr-1.5" />
          Take a Test
        </button>
        <button
          onClick={() => setActiveTab('tests')}
          className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
            activeTab === 'tests'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <ClipboardCheck className="w-4 h-4 mr-1.5" />
          All Tests ({assessments.length})
        </button>
        <button
          onClick={() => setActiveTab('attempts')}
          className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center ${
            activeTab === 'attempts'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Trophy className="w-4 h-4 mr-1.5" />
          My Attempts ({myAttempts.length})
        </button>
      </div>

      {/* Take a Test Tab */}
      {activeTab === 'generate' && (
        <div>
          {/* Loading overlay */}
          {generating && (
            <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl text-center">
              <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-purple-800">Loading {generatingLabel}...</h3>
              <p className="text-sm text-purple-600 mt-1">
                {generatingLabel.includes('Practice')
                  ? 'AI is generating a fresh paper section by section. This may take 1-2 minutes.'
                  : 'Loading real previous year questions...'
                }
              </p>
            </div>
          )}

          {/* Previous Year Papers Section (only for exams that have PYQ) */}
          {currentExam.hasPYQ && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Previous Year Papers
              </h2>
              <p className="text-sm text-gray-500 mb-4">Real {currentExam.name} questions from official exam papers</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {currentExam.pyqYears.map(year => (
                  <button
                    key={year}
                    onClick={() => handleGeneratePYQ(year)}
                    disabled={generating}
                    className="relative bg-white border-2 border-blue-100 rounded-xl p-5 hover:border-blue-400 hover:shadow-lg transition-all text-center disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    <div className="text-3xl font-bold text-blue-600 group-hover:text-blue-700">{year}</div>
                    <p className="text-xs text-gray-500 mt-1">{currentExam.name} Paper</p>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-medium rounded-full">{currentExam.questions} Questions</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Divider (only show if PYQ section exists) */}
          {currentExam.hasPYQ && (
            <div className="relative mb-8">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center"><span className="bg-gray-50 px-4 text-sm text-gray-400">OR</span></div>
            </div>
          )}

          {/* AI Practice Mode Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
              <Zap className="w-5 h-5 mr-2 text-purple-600" />
              AI Practice Mode
            </h2>
            <p className="text-sm text-gray-500 mb-4">Fresh AI-generated {currentExam.name} questions every time — different paper each attempt</p>
            <p className="text-xs text-gray-400 mb-4">{currentExam.sections}</p>

            {/* Difficulty Selector */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-medium text-gray-700">Difficulty:</span>
              <div className="flex gap-2">
                {[
                  { key: 'easy', label: 'Easy', color: 'green', desc: 'Basic concepts' },
                  { key: 'medium', label: 'Medium', color: 'yellow', desc: 'Exam level' },
                  { key: 'hard', label: 'Hard', color: 'red', desc: 'Advanced' },
                ].map(d => (
                  <button
                    key={d.key}
                    onClick={() => setSelectedDifficulty(d.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedDifficulty === d.key
                        ? d.color === 'green' ? 'bg-green-600 text-white shadow-md'
                          : d.color === 'yellow' ? 'bg-yellow-500 text-white shadow-md'
                          : 'bg-red-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {d.label}
                    <span className={`block text-[10px] font-normal ${selectedDifficulty === d.key ? 'text-white/80' : 'text-gray-400'}`}>{d.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGeneratePractice}
              disabled={generating}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Generate {currentExam.name} Practice Paper
              <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">{currentExam.questions} Questions</span>
            </button>
          </div>
        </div>
      )}

      {/* All Tests Tab */}
      {activeTab === 'tests' && (
        <div>
          {assessments.length === 0 ? (
            <div className="text-center py-16">
              <ClipboardCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-500">No mock tests yet</h3>
              <p className="text-gray-400 mt-1">Take a test from the first tab to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assessments.map(a => {
                const attempts = getAttemptForAssessment(a.id);
                const bestScore = getBestScore(attempts);
                const isPYQ = a.title?.includes('Previous Year');
                return (
                  <div
                    key={a.id}
                    onClick={() => navigate(`/lms/assessments/${a.id}`)}
                    className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPYQ ? 'bg-blue-50' : 'bg-purple-50'}`}>
                        {isPYQ ? <FileText className="w-5 h-5 text-blue-600" /> : <Sparkles className="w-5 h-5 text-purple-600" />}
                      </div>
                      {bestScore !== null && (
                        <span className={`text-sm font-bold ${bestScore >= 60 ? 'text-green-600' : bestScore >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                          <Trophy className="w-4 h-4 inline mr-1" />{bestScore}%
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{a.title}</h3>
                    <p className="text-xs text-gray-500 mb-3">{a.description}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center"><HelpCircle className="w-3.5 h-3.5 mr-1" /> {a.question_count || '?'} questions</span>
                      <span className="flex items-center"><Clock className="w-3.5 h-3.5 mr-1" /> {a.time_limit_minutes} min</span>
                    </div>
                    {attempts.length > 0 && (
                      <p className="text-xs text-gray-400 mt-2">{attempts.length} attempt{attempts.length > 1 ? 's' : ''}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* My Attempts Tab */}
      {activeTab === 'attempts' && (
        <div>
          {myAttempts.length === 0 ? (
            <div className="text-center py-16">
              <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-500">No attempts yet</h3>
              <p className="text-gray-400 mt-1">Take a test to see your results here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myAttempts.map(attempt => (
                <div
                  key={attempt.id}
                  onClick={() => navigate(`/lms/assessments/${attempt.assessment_id}`)}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow cursor-pointer flex items-center justify-between"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">{attempt.assessment_title}</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(attempt.started_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {attempt.status === 'graded' && attempt.score !== null && (
                        <span className="ml-3 font-medium">
                          Score: {attempt.score}/{attempt.max_score}
                          ({attempt.max_score > 0 ? Math.round((attempt.score / attempt.max_score) * 100) : 0}%)
                        </span>
                      )}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    attempt.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {attempt.status === 'graded' ? 'Completed' : 'In Progress'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MockTests;
